// CashFlowFilterPanel.js
// Expandable filter panel for CashFlow page with advanced search options

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Chip,
  InputAdornment,
  Badge,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import { format } from 'date-fns';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@tanstack/react-query';
import { getGeneralTransactionTags } from '../services/cashflowService';

// Default filter state
const defaultFilters = {
  search: '',
  amountMin: '',
  amountMax: '',
  tagId: '',
  paymentMethod: '',
  startDate: null,
  endDate: null,
};

// Payment method options
const paymentMethodOptions = [
  { value: '', label: 'הכל' },
  { value: 'cash', label: 'מזומן' },
  { value: 'check', label: "צ'ק" },
  { value: 'credit_card', label: 'אשראי' },
  { value: 'bank_transfer', label: 'העברה בנקאית' },
  { value: 'other', label: 'אחר' },
];

const CashFlowFilterPanel = ({
  onApply,
  appliedFilters = defaultFilters,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Use refs to store current values (no state delays)
  const searchRef = useRef(null);
  const amountMinRef = useRef(null);
  const amountMaxRef = useRef(null);
  const paymentMethodRef = useRef(appliedFilters.paymentMethod || '');
  const tagIdRef = useRef(appliedFilters.tagId || '');

  // Date pickers need controlled state
  const [startDate, setStartDate] = useState(appliedFilters.startDate || null);
  const [endDate, setEndDate] = useState(appliedFilters.endDate || null);

  // Fetch tags for dropdown
  const { data: allTags = [] } = useQuery({
    queryKey: ['general-transaction-tags'],
    queryFn: () => getGeneralTransactionTags(),
    staleTime: 60 * 1000,
  });

  const tags = allTags;

  // Sync refs with applied filters when panel opens
  useEffect(() => {
    if (isOpen) {
      if (searchRef.current) searchRef.current.value = appliedFilters.search || '';
      if (amountMinRef.current) amountMinRef.current.value = appliedFilters.amountMin || '';
      if (amountMaxRef.current) amountMaxRef.current.value = appliedFilters.amountMax || '';
      paymentMethodRef.current = appliedFilters.paymentMethod || '';
      tagIdRef.current = appliedFilters.tagId || '';
      setStartDate(appliedFilters.startDate || null);
      setEndDate(appliedFilters.endDate || null);
    }
  }, [isOpen, appliedFilters]);

  // Count active filters
  const countActiveFilters = (filters) => {
    let count = 0;
    if (filters.search) count++;
    if (filters.amountMin || filters.amountMax) count++;
    if (filters.tagId) count++;
    if (filters.paymentMethod) count++;
    if (filters.startDate || filters.endDate) count++;
    return count;
  };

  const activeFilterCount = countActiveFilters(appliedFilters);

  // Get current filter values directly from DOM/refs
  const getCurrentFilters = useCallback(() => {
    return {
      search: searchRef.current?.value || '',
      amountMin: amountMinRef.current?.value || '',
      amountMax: amountMaxRef.current?.value || '',
      paymentMethod: paymentMethodRef.current || '',
      tagId: tagIdRef.current || '',
      startDate: startDate,
      endDate: endDate,
    };
  }, [startDate, endDate]);

  // Apply filters - reads directly from DOM/refs
  const handleApply = useCallback(() => {
    const filters = getCurrentFilters();
    onApply(filters);
    setIsOpen(false);
  }, [getCurrentFilters, onApply]);

  // Handle Enter key to apply filters
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    }
  }, [handleApply]);

  // Cancel and revert
  const handleCancel = () => {
    setIsOpen(false);
  };

  // Reset all filters in the form
  const handleReset = () => {
    if (searchRef.current) searchRef.current.value = '';
    if (amountMinRef.current) amountMinRef.current.value = '';
    if (amountMaxRef.current) amountMaxRef.current.value = '';
    paymentMethodRef.current = '';
    tagIdRef.current = '';
    setStartDate(null);
    setEndDate(null);
    // Force re-render for select components
    setIsOpen(true);
  };

  // Clear all applied filters
  const handleClearAll = () => {
    onApply(defaultFilters);
    setIsOpen(false);
  };

  // Handle select changes (store in ref)
  const handlePaymentMethodChange = (event) => {
    paymentMethodRef.current = event.target.value;
  };

  const handleTagChange = (event) => {
    tagIdRef.current = event.target.value;
  };

  // Get display text for active filter chip
  const getFilterChipLabel = () => {
    const parts = [];
    if (appliedFilters.search) {
      parts.push(`חיפוש: "${appliedFilters.search}"`);
    }
    if (appliedFilters.amountMin || appliedFilters.amountMax) {
      const min = appliedFilters.amountMin || '0';
      const max = appliedFilters.amountMax || '...';
      parts.push(`סכום: ${min}-${max}`);
    }
    if (appliedFilters.tagId) {
      const tag = tags.find((t) => t.TagID === parseInt(appliedFilters.tagId));
      if (tag) parts.push(`קטגוריה: ${tag.Name}`);
    }
    if (appliedFilters.paymentMethod) {
      const pm = paymentMethodOptions.find((p) => p.value === appliedFilters.paymentMethod);
      if (pm) parts.push(`אמצעי: ${pm.label}`);
    }
    if (appliedFilters.startDate || appliedFilters.endDate) {
      const start = appliedFilters.startDate ? format(appliedFilters.startDate, 'dd/MM/yy') : '...';
      const end = appliedFilters.endDate ? format(appliedFilters.endDate, 'dd/MM/yy') : '...';
      parts.push(`תאריך: ${start}-${end}`);
    }
    return parts.join(' | ');
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Toggle Button and Active Filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Badge badgeContent={activeFilterCount} color="primary" invisible={activeFilterCount === 0}>
          <Button
            variant={isOpen ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={() => setIsOpen(!isOpen)}
            size="medium"
            sx={{ direction: 'ltr' }}
          >
            סינון מתקדם
          </Button>
        </Badge>

        {/* Active filter chips */}
        {activeFilterCount > 0 && !isOpen && (
          <Chip
            label={getFilterChipLabel()}
            onDelete={handleClearAll}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ maxWidth: 400, textOverflow: 'ellipsis', direction: 'ltr' }}
          />
        )}
      </Box>

      {/* Collapsible Filter Panel */}
      <Collapse in={isOpen}>
        <Paper sx={{ mt: 2, p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* Search Field */}
            <TextField
              inputRef={searchRef}
              size="small"
              label="חיפוש"
              placeholder="שם, הערות או מס' חשבונית"
              defaultValue={appliedFilters.search || ''}
              onKeyDown={handleKeyDown}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Amount Min */}
            <TextField
              inputRef={amountMinRef}
              size="small"
              label="סכום מינימלי"
              type="number"
              defaultValue={appliedFilters.amountMin || ''}
              onKeyDown={handleKeyDown}
              sx={{ width: 130 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">₪</InputAdornment>,
              }}
            />

            {/* Amount Max */}
            <TextField
              inputRef={amountMaxRef}
              size="small"
              label="סכום מקסימלי"
              type="number"
              defaultValue={appliedFilters.amountMax || ''}
              onKeyDown={handleKeyDown}
              sx={{ width: 130 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">₪</InputAdornment>,
              }}
            />

            {/* Payment Method */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>אמצעי תשלום</InputLabel>
              <Select
                key={appliedFilters.paymentMethod || 'all'}
                defaultValue={appliedFilters.paymentMethod || ''}
                onChange={handlePaymentMethodChange}
                label="אמצעי תשלום"
              >
                {paymentMethodOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Tag/Category */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>קטגוריה</InputLabel>
              <Select
                defaultValue={appliedFilters.tagId || ''}
                onChange={handleTagChange}
                label="קטגוריה"
              >
                <MenuItem value="">הכל</MenuItem>
                {tags.map((tag) => (
                  <MenuItem key={tag.TagID} value={tag.TagID}>
                    {tag.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Range */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
              <DatePicker
                label="מתאריך"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 170, direction: 'ltr' },
                    onKeyDown: handleKeyDown,
                  },
                  field: { clearable: true },
                }}
              />
              <DatePicker
                label="עד תאריך"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 170, direction: 'ltr' },
                    onKeyDown: handleKeyDown,
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button
              size="small"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={handleReset}
              sx={{ direction: 'ltr' }}
            >
              אפס
            </Button>
            <Button size="small" color="inherit" onClick={handleCancel}>
              ביטול
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              החל סינון
            </Button>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default CashFlowFilterPanel;
